import {
  GoogleAuthError,
  SheetInfo,
  sheetsApi,
  SheetsList,
} from '../services/api';
import { notify } from '../ui';

interface ExportSection {
  key: 'units' | 'appeals' | 'calls' | 'tasks';
  title: string;
  type: SheetInfo['exportType'];
  sheets: {
    name: string;
    exportFileId: string;
    updatedAt: string;
  }[];
}

export class DashboardExportCtrl {
  static $inject = ['$scope', '$window', '$mdDialog', '$translate'];

  exports: { exportFileId: string }[] = [];
  loaded = false;
  btnLoadingOn = false;

  sectionTitles: { [key: string]: string } = {};
  declare exportSections: ExportSection[];

  constructor(
    private $scope: angular.IRootScopeService,
    private $window: angular.IWindowService,
    private $mdDialog: angular.material.IDialogService,
    private $translate: angular.translate.ITranslateService,
  ) {
    this.exportSections = [
      {
        key: 'units',
        title: $translate.instant('reports.page.sidebar.export.units.title'),
        type: sheetsApi.SHEET_TYPE.UNITS,
        sheets: [],
      },
      {
        key: 'appeals',
        title: $translate.instant('reports.page.sidebar.export.appeals.title'),
        type: sheetsApi.SHEET_TYPE.APPEALS,
        sheets: [],
      },
      {
        key: 'tasks',
        title: $translate.instant('reports.page.sidebar.export.tasks.title'),
        type: sheetsApi.SHEET_TYPE.TASKS,
        sheets: [],
      },
      {
        key: 'calls',
        title: $translate.instant('reports.page.sidebar.export.calls.title'),
        type: sheetsApi.SHEET_TYPE.CALLS,
        sheets: [],
      },
    ];

    this.getExports();
  }

  async getExports() {
    try {
      const resp = await sheetsApi.getList();

      this.$scope.$apply(() => {
        this.populateSections(resp);
      });
    } catch (error) {
      if (error instanceof GoogleAuthError) {
        // just skip. wait till user will press some button

        return;
      }

      notify.error('Error getting exports', error);
    } finally {
      this.$scope.$apply(() => {
        this.loaded = true;
      });
    }
  }

  async requestExport(section: ExportSection) {
    if (this.btnLoadingOn) {
      return;
    }

    this.btnLoadingOn = true;

    try {
      const resp = await sheetsApi.requestExport({
        type: section.type,
      });

      this.$scope.$apply(() => {
        this.populateSections(resp);
      });
    } catch (error) {
      if (error instanceof GoogleAuthError) {
        this.showAuthGoogleDriveDialog(error.getUrl());

        return;
      }

      notify.error(
        `Error requesting export: ${error?.message || 'unexpected error'}`,
        error,
      );
    } finally {
      this.$scope.$apply(() => {
        this.btnLoadingOn = false;
      });
    }
  }

  openTable(fileId: string) {
    const googleDriveUrl = `https://docs.google.com/spreadsheets/d/${fileId}/edit`;

    this.$window.open(googleDriveUrl, '_blank');
  }

  private populateSections(resp: SheetsList) {
    this.exportSections.forEach((section) => {
      if (!resp.data.spreadsheets[section.key]) {
        return;
      }

      section.sheets = resp.data.spreadsheets[section.key].map(
        (item: SheetInfo) => {
          let name = this.$translate.instant(
            'reports.page.sidebar.export.units.title',
          );

          if (item.priceDate && /^\d{4}/.test(item.priceDate)) {
            name = item.priceDate.slice(0, 4);
          }

          return {
            name,
            exportFileId: item.exportFileId,
            updatedAt: item.dateCreated,
          };
        },
      );
    });
  }

  private showAuthGoogleDriveDialog(authUrl: string) {
    const { $translate, $mdDialog } = this;

    $mdDialog
      .show(
        $mdDialog
          .confirm()
          .title(
            $translate.instant('reports.page.sidebar.export.data.dialog.title'),
          )
          .textContent(
            $translate.instant(
              'reports.page.sidebar.export.data.dialog.content',
            ),
          )
          .ariaLabel('Alert Dialog')
          .ok($translate.instant('reports.page.sidebar.export.data.dialog.ok'))
          .cancel($translate.instant('general.cancel.long')),
      )
      .then(
        () => {
          const redirectUrl = 'dashboard%23/dashboard/export';

          this.$window.location.href = `${
            this.$window.location.origin + authUrl
          }/?redirect=${redirectUrl}`;
        },
        () => {
          // in case No
        },
      );
  }
}
